import type { PayloadAction } from "@reduxjs/toolkit";

/**
 * Shared parts of DiscountSlice — plan §5.2. State is IDENTICAL on both platforms (4 fields);
 * 6 of web's 9 and mobile's 8 reducers are identical and live here.
 *
 * Authored per platform instead:
 * - `applyDiscountSuccess` — web stamps `isOtpRequired: true` onto every discount when the
 *   payload says `OTPRequired`; mobile stores the payload as-is.
 * - `addDiscountApprovalData` — same helper call, different way of snapshotting the draft:
 *   web uses RTK's `current()`, mobile a `deepClone()`. Preserved verbatim (see each file).
 * - web's empty `userLogout`/`supportLogout` cases.
 *
 * `getExternalDiscounts` was in that list until 2026-09-08 — see its own note below.
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger. `any` makes RTK
 * infer the NO-PAYLOAD overload, so every `dispatch(x(payload))` stops compiling.
 */

/**
 * ⚠️ Declared in apps/web only. `apps/mobile`'s DiscountSlice REFERENCES this type without
 * declaring or importing it, which is one of the TS2304 rows in the mobile baseline. Hoisting
 * it here gives both platforms the same (already intended) type and removes that error — a
 * type-level fix with no runtime effect. `--verify` will report it as an added type on the
 * mobile side; that is this note, not drift.
 */
export type DiscountApprovalType = {
  isApproved: boolean,
  mid: string,
  isApprovalRequired: boolean,
  isRequested: boolean,
  approvalRequestId?: string
  currentStatus?: string,
  totalDiscount?: number,
  value?: string,
  requestType?: string,
  approvalData?: any
}

export interface SharedDiscountState {
  discountInfo: {
    [key: string]: any
  },
  isDiscountApplying: boolean
  discountApproval: DiscountApprovalType[]
  statusModalConfig: { showStatusModal: boolean; isRequestPending: boolean; };
}

export const sharedInitialState: SharedDiscountState = {
  discountInfo: {},
  isDiscountApplying: false,
  discountApproval: [],
  statusModalConfig: { showStatusModal: false, isRequestPending: false }
};

type S = typeof sharedInitialState;

export const sharedReducers = {
  applyDiscountApi: (state: S, action: PayloadAction<any>) => {
    state.isDiscountApplying = true;
  },
  applyDiscountFail: (state: S) => {
    state.isDiscountApplying = false;
  },
  updateDiscountSlice: (state: S, action: PayloadAction<any>) => {
    return action.payload;
  },
  updateDiscountClosed: (state: S, action: PayloadAction<any>) => {},
  checkDiscountStatus: (state: S, action: PayloadAction<any>) => { },
  setShowDiscountStatusModal: (state: S, action: PayloadAction<any>) => {
    state.statusModalConfig = action.payload;
  },
  /**
   * Fetch the partner offers available to this customer against this sale. Answered by the
   * discount saga's `getExternalDiscountsList`, which hands the response to the callback — the
   * offers are never stored on this slice.
   *
   * ⚠️ MOVED here from `index.desktop.ts` on 2026-09-08, with `packages/core/discounts`. It was
   * web-only because only web had an offers surface, not because the action has a platform half
   * — an empty reducer has nothing to split. Both saga roots now register a watcher for it.
   */
  getExternalDiscounts: (state: S, action: PayloadAction<any>) => {},
};
